import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SpreadPhotoInfo from './SpreadPhotoInfo';
import './PagePreviewViewer.css';

// APERCU PLEIN ECRAN (atelier ET Apercu final) — phase « Apercu et lisibilite de la page »,
// 2026-10-12, maquette « Atelier Bookipix » (.pv). Remplace les deux fenetres precedentes.
//
//  - fond sombre OPAQUE (plus rien de l'atelier ne se lit derriere) ;
//  - barre fine en haut : titre, interrupteur « Voir les marges et le pli » (touche M),
//    « Fermer » toujours visible ;
//  - la page remplit au moins 90 % de la hauteur disponible en mode « Ajuste » ;
//  - deux niveaux : Ajuste (defaut) et 100 % (taille reelle : 21 cm = 794 px) ;
//    boutons en bas, clic sur la page = Ajuste <-> 100 %, touches + / - / 0, pincement ;
//    zoome : deplacement a la souris (glisser) ou par defilement ;
//  - fleches sur les cotes, indicateur « 7 / 30 » discret en bas, fleches du clavier, Echap ;
//  - au zoom, les photos sont rechargees en pleine resolution (les apercus ecran sont
//    plafonnes a 1600 px) ; badge d'alerte si une photo de la page est en basse resolution ;
//  - marges et zone du pli superposables.
//
// Le contenu des pages reste CELUI DU SERVEUR (iframes srcdoc a la taille naturelle, mises a
// l'echelle par un seul transform) : ce qu'on voit est ce qui sera imprime.
const ZOOMS = ['fit', 1];
const PADDING_PX = 8;
const DRAG_THRESHOLD_PX = 4;
const PINCH_STEP = 1.25;

function PagePreviewViewer({
  title,
  // [{ key, html, label, fold: 'left' | 'right' | null }] — 1 ou 2 pages, de gauche a droite.
  pages,
  pageWidthPx,
  pageHeightPx,
  spineGapPx = 12,
  counter,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onFirst,
  onLast,
  onClose,
  // Marges de la page et zone du pli, en mm (superposables).
  pageWidthMm,
  marginMm = 0,
  foldMm = 12,
  lowRes = false,
  // [{ preview, original }] : au zoom, l'apercu ecran est remplace par l'original.
  photoUpgrades = [],
  spreadPhoto = false
}) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState('fit');
  const [showMargins, setShowMargins] = useState(false);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const scrollRef = useRef(null);
  const closeRef = useRef(null);
  const contentRef = useRef(null);
  const iframesRef = useRef(new Set());
  const dragRef = useRef(null);
  const didDragRef = useRef(false);
  const pinchRef = useRef(null);
  const pointersRef = useRef(new Map());
  const latest = useRef({});

  const count = pages.length || 1;
  const contentW = count * pageWidthPx + (count - 1) * spineGapPx;
  const contentH = pageHeightPx;
  // Boite de reference : toujours une double page, pour qu'une page seule (couverture) ne soit
  // pas affichee deux fois plus grande qu'une page de double page.
  // Fenetre etroite (telephone) : une page seule remplit la largeur au lieu de rester a demi-taille.
  const refW = count === 1 && box.width < 700 ? pageWidthPx : 2 * pageWidthPx + spineGapPx;
  const availW = Math.max(0, box.width - 2 * PADDING_PX);
  const availH = Math.max(0, box.height - 2 * PADDING_PX);
  const fitScale = availW > 0 && availH > 0 ? Math.min(availW / refW, availH / contentH) : 1;
  const scale = zoom === 'fit' ? fitScale : zoom;
  const sizerW = Math.round(contentW * scale);
  const sizerH = Math.round(contentH * scale);

  const goZoom = useCallback((next) => setZoom(next), []);
  const step = useCallback((direction) => {
    setZoom((current) => ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, ZOOMS.indexOf(current) + direction))]);
  }, []);
  latest.current = { step, canPrev, canNext, onPrev, onNext, onFirst, onLast, onClose };

  // Mesure de la zone disponible.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Plein ecran : plus de defilement derriere, le site masque son en-tete, le focus entre dans la fenetre.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const opener = document.activeElement;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('has-fullscreen-viewer');
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('has-fullscreen-viewer');
      if (opener && opener.focus) { try { opener.focus(); } catch (_error) { /* element disparu */ } }
    };
  }, []);

  // Clavier : fleches, Echap, + / - / 0, M. Tab reste dans la fenetre.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const state = latest.current;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); state.onClose(); return; }
      if (event.key === 'ArrowRight') { event.preventDefault(); if (state.canNext) state.onNext(); return; }
      if (event.key === 'ArrowLeft') { event.preventDefault(); if (state.canPrev) state.onPrev(); return; }
      if (event.key === 'Home' && state.onFirst) { event.preventDefault(); state.onFirst(); return; }
      if (event.key === 'End' && state.onLast) { event.preventDefault(); state.onLast(); return; }
      if (event.key === '+' || event.key === '=') { event.preventDefault(); state.step(1); return; }
      if (event.key === '-' || event.key === '_') { event.preventDefault(); state.step(-1); return; }
      if (event.key === '0') { event.preventDefault(); setZoom('fit'); return; }
      if (event.key === 'm' || event.key === 'M') { event.preventDefault(); setShowMargins((value) => !value); return; }
      if (event.key === 'Tab' && rootRef.current) {
        const focusable = [...rootRef.current.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Nouvelle vue : on repart du centre. Le zoom, lui, est conserve en tournant les pages.
  const viewKey = pages.map((page) => page.key).join('|');
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) { el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2; el.scrollTop = (el.scrollHeight - el.clientHeight) / 2; }
  }, [viewKey, zoom, sizerW, sizerH]);

  // Pleine resolution au zoom : on remplace, dans les pages, l'apercu ecran par l'original.
  useEffect(() => {
    if (zoom === 'fit' || !photoUpgrades.length) return undefined;
    const map = new Map(photoUpgrades.filter((entry) => entry.preview && entry.original && entry.preview !== entry.original).map((entry) => [entry.preview, entry.original]));
    if (!map.size) return undefined;
    const apply = () => {
      iframesRef.current.forEach((frame) => {
        try {
          frame.contentDocument.querySelectorAll('img').forEach((img) => {
            const original = map.get(img.getAttribute('src'));
            if (original) { img.setAttribute('src', original); img.dataset.fullres = '1'; }
          });
        } catch (_error) { /* page pas encore chargee */ }
      });
    };
    apply();
    const frames = [...iframesRef.current];
    frames.forEach((frame) => frame.addEventListener('load', apply));
    const timer = setInterval(apply, 800);
    return () => { clearInterval(timer); frames.forEach((frame) => frame.removeEventListener('load', apply)); };
  }, [zoom, photoUpgrades, viewKey]);

  const registerFrame = useCallback((node) => {
    if (node) iframesRef.current.add(node);
  }, []);
  useEffect(() => { iframesRef.current = new Set([...iframesRef.current].filter((node) => node.isConnected)); }, [viewKey]);

  // --- gestes : glisser pour deplacer (souris), pincement (tactile), clic = Ajuste <-> 100 % ---
  const onPointerDown = (event) => {
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { start: Math.hypot(a.x - b.x, a.y - b.y) || 1, done: false };
      return;
    }
    didDragRef.current = false;
    if (event.pointerType === 'mouse' && event.button === 0 && zoom !== 'fit' && scrollRef.current) {
      dragRef.current = { x: event.clientX, y: event.clientY, left: scrollRef.current.scrollLeft, top: scrollRef.current.scrollTop };
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch (_error) { /* deja libere */ }
    }
  };
  const onPointerMove = (event) => {
    if (pointersRef.current.has(event.pointerId)) pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pinchRef.current && pointersRef.current.size >= 2 && !pinchRef.current.done) {
      const [a, b] = [...pointersRef.current.values()];
      const ratio = (Math.hypot(a.x - b.x, a.y - b.y) || 1) / pinchRef.current.start;
      if (ratio > PINCH_STEP) { pinchRef.current.done = true; step(1); }
      else if (ratio < 1 / PINCH_STEP) { pinchRef.current.done = true; step(-1); }
      return;
    }
    const drag = dragRef.current;
    if (!drag || !scrollRef.current) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) didDragRef.current = true;
    scrollRef.current.scrollLeft = drag.left - dx;
    scrollRef.current.scrollTop = drag.top - dy;
  };
  const onPointerEnd = (event) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) {
      if (pinchRef.current && pinchRef.current.done) didDragRef.current = true; // le pincement ne compte pas comme un clic
      pinchRef.current = null;
    }
    dragRef.current = null;
  };
  // Clic SUR la page = Ajuste <-> 100 %. Le clic est ecoute sur la zone de defilement (le pointeur y est
  // capture pendant un glisser) et on verifie qu'il tombe bien sur la page, pas sur le fond.
  const onAreaClick = (event) => {
    if (didDragRef.current) { didDragRef.current = false; return; }
    const rect = contentRef.current && contentRef.current.getBoundingClientRect();
    if (!rect) return;
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (inside) setZoom((current) => (current === 'fit' ? 1 : 'fit'));
  };

  const marginPx = pageWidthMm ? (pageWidthPx * marginMm) / pageWidthMm : 0;
  const foldPx = pageWidthMm ? (pageWidthPx * foldMm) / pageWidthMm : 0;
  const zoomLabels = useMemo(() => ({ fit: t('pageViewer.fit'), 1: t('pageViewer.actual') }), [t]);

  return (
    <div ref={rootRef} className="ppv" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ppv-top">
        <b className="ppv-title">{title}</b>
        {spreadPhoto && <SpreadPhotoInfo tone="dark" placement="bottom" align="start" layflat={foldMm === 0} />}
        <span className="ppv-note" title={t('pageViewer.dimensionsNote')}>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6z" fill="none" stroke="currentColor" strokeWidth="1.9" /><path d="m9 12 2 2 4-4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span className="ppv-note-text">{t('pageViewer.dimensionsNote')}</span>
        </span>
        <div className="ppv-right">
          <button
            type="button"
            className="ppv-toggle"
            aria-pressed={showMargins}
            title={t('pageViewer.marginsKey')}
            onClick={() => setShowMargins((value) => !value)}
          >
            <span className="ppv-long">{t('pageViewer.margins')}</span>
            <span className="ppv-short">{t('pageViewer.marginsShort')}</span>
          </button>
          <button ref={closeRef} type="button" className="ppv-close" onClick={onClose}>{t('pageViewer.close')}</button>
        </div>
      </div>

      <div ref={bodyRef} className={`ppv-body ${zoom !== 'fit' ? 'is-zoomed' : ''}`}>
        <div
          ref={scrollRef}
          className="ppv-scroll"
          tabIndex={zoom !== 'fit' ? 0 : -1}
          role="region"
          aria-label={t('pageViewer.pageArea')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onClick={onAreaClick}
          title={zoom === 'fit' ? t('pageViewer.clickActual') : t('pageViewer.clickFit')}
        >
          <div className="ppv-sizer" style={{ width: sizerW, height: sizerH }}>
            <div
              ref={contentRef}
              className="ppv-content"
              style={{ width: contentW, height: contentH, transform: `scale(${scale})`, gap: spineGapPx }}
            >
              {pages.map((page) => (
                <div key={page.key} className="ppv-page" style={{ width: pageWidthPx, height: pageHeightPx }}>
                  {page.html ? (
                    <iframe ref={registerFrame} title={page.label} srcDoc={page.html} className="ppv-frame" tabIndex={-1} />
                  ) : (
                    <div className="ppv-placeholder" />
                  )}
                  {showMargins && (
                    <div className="ppv-safe" aria-hidden="true">
                      {marginPx > 0 && <i className="ppv-safe-box" style={{ inset: marginPx }} />}
                      {page.fold && foldPx > 0 && (
                        <i className={`ppv-safe-fold is-${page.fold}`} style={{ width: foldPx }} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <button type="button" className="ppv-arrow is-left" onClick={onPrev} disabled={!canPrev} aria-label={t('pageViewer.previous')}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
        </button>
        <button type="button" className="ppv-arrow is-right" onClick={onNext} disabled={!canNext} aria-label={t('pageViewer.next')}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
        </button>
      </div>

      <div className="ppv-bottom">
        <div className="ppv-seg" role="group" aria-label={t('pageViewer.zoomGroup')}>
          {ZOOMS.map((level) => (
            <button key={String(level)} type="button" aria-pressed={zoom === level} onClick={() => goZoom(level)} title={level === 1 ? t('pageViewer.actualTitle') : undefined}>
              {zoomLabels[level]}
            </button>
          ))}
        </div>
        <span className="ppv-counter" aria-live="polite">{counter}</span>
        {showMargins && (
          <span className="ppv-legend">
            <span><i className="ppv-legend-margin" />{t('pageViewer.legendMargin')}</span>
            {foldMm > 0 && <span><i className="ppv-legend-fold" />{t('pageViewer.legendFold', { mm: foldMm })}</span>}
          </span>
        )}
        {lowRes && zoom !== 'fit' && (
          <span className="ppv-warn" role="status">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" /></svg>
            {t('pageViewer.lowRes')}
          </span>
        )}
      </div>
    </div>
  );
}

export default PagePreviewViewer;
